import { useTranslations } from "use-intl";

import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";

interface ReleaseFormatFeatureTagsProps {
  mediaFeatures?: string[] | null;
  className?: string;
  tagClassName?: string;
  /**
   * Show at most this many tags, cautionary first, then special editions, then
   * neutral ones; the rest collapse into a "+N" chip whose title lists them.
   */
  maxVisible?: number;
}

// Three tones only, so the badge row reads calmly instead of as a rainbow:
//   - special/collectible editions → amber (worth highlighting)
//   - cautionary states (unofficial, withdrawn) → red (a real signal)
//   - everything else (reissues, media formats, pre-release) → neutral
const SPECIAL_TONE = "bg-amber-500/10 text-amber-300 border border-amber-500/25";
const CAUTION_TONE = "bg-red-500/10 text-red-300 border border-red-500/25";

const tagColorMap: Record<string, string> = {
  // Special / collectible editions — the only feature worth an accent
  limited_edition: SPECIAL_TONE,
  deluxe_edition: SPECIAL_TONE,
  special_edition: SPECIAL_TONE,
  record_store_day: SPECIAL_TONE,
  numbered: SPECIAL_TONE,
  picture_disc: SPECIAL_TONE,
  box_set: SPECIAL_TONE,

  // Cautionary — unofficial or pulled from circulation
  non_official_status: CAUTION_TONE,
  partially_unofficial: CAUTION_TONE,
  bootleg: CAUTION_TONE,
  withdrawn: CAUTION_TONE,
  cancelled: CAUTION_TONE,
  expunged: CAUTION_TONE,
};

// Reissue, repress, remastered, media formats, pre-release, etc. all land here.
const defaultTagColor = "bg-secondary text-muted-foreground border border-border/60";

function tagPriority(tag: string): number {
  if (tagColorMap[tag] === CAUTION_TONE) return 0;
  if (tagColorMap[tag] === SPECIAL_TONE) return 1;
  return 2;
}

function limitTags(tags: string[], maxVisible: number | undefined) {
  if (maxVisible === undefined || tags.length <= maxVisible) return { visible: tags, hidden: [] };
  const ordered = [...tags].sort((a, b) => tagPriority(a) - tagPriority(b));
  return { visible: ordered.slice(0, maxVisible), hidden: ordered.slice(maxVisible) };
}

/** Render backend-normalized release format-feature tags. */
export function ReleaseFormatFeatureTags({
  mediaFeatures,
  className,
  tagClassName,
  maxVisible,
}: ReleaseFormatFeatureTagsProps) {
  const tFeature = useTranslations("shared.mediaFeatures");
  const tFormat = useTranslations("shared.formats");
  const tRelease = useTranslations("release");

  if (!mediaFeatures?.length) return null;

  const tags = [...new Set(mediaFeatures.map((feature) => feature.trim()).filter(Boolean))];

  if (tags.length === 0) return null;

  const formatLabel = (key: string) => {
    const featureKey = key as Parameters<typeof tFeature>[0];
    if (tFeature.has(featureKey)) return tFeature(featureKey);

    const formatKey = key as Parameters<typeof tFormat>[0];
    if (tFormat.has(formatKey)) return tFormat(formatKey);

    const releaseKey = key as Parameters<typeof tRelease>[0];
    if (tRelease.has(releaseKey)) return tRelease(releaseKey);

    const releaseTypeKey = `releaseType_${key}` as Parameters<typeof tRelease>[0];
    if (tRelease.has(releaseTypeKey)) return tRelease(releaseTypeKey);

    return formatCatalogTaxonomyLabel(key);
  };

  const { visible, hidden } = limitTags(tags, maxVisible);

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {visible.map((feature) => (
        <span
          key={feature}
          className={cn(
            "px-2 py-0.5 text-xs font-semibold rounded",
            tagColorMap[feature] ?? defaultTagColor,
            tagClassName,
          )}
        >
          {formatLabel(feature)}
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          title={hidden.map(formatLabel).join(", ")}
          className={cn("px-2 py-0.5 text-xs font-semibold rounded", defaultTagColor, tagClassName)}
        >
          +{hidden.length}
        </span>
      )}
    </div>
  );
}
